<template>
  <div class="w-full rounded-lg border border-[#F0F0F4] p-4" data-testid="approval-flow-preview">
    <h3 class="text-lg text-left font-bold leading-6 tracking-[0.09px] text-textPrimary mb-4">
      Pré-visualização do fluxo de aprovação
    </h3>
    <div class="overflow-x-auto">
      <div class="flex items-start min-w-max px-2">
        <div class="flex flex-col items-center gap-1 min-w-[80px]">
          <div class="w-10 h-10 rounded-full flex items-center justify-center z-10 bg-[#267D92]">
            <v-icon :icon="mdiPlay" color="white" size="20" />
          </div>
          <span class="text-[14px] text-[#4A4A4A] text-center leading-[1.2] mt-2">
            Pedido<br />Realizado
          </span>
        </div>

        <template v-for="(group, groupIndex) in groupedLayers" :key="groupIndex">
          <div class="flex items-center h-10 min-w-[60px] grow">
            <div class="h-[3px] bg-[#BCBCCD] grow"></div>
            <template v-if="groupIndex > 0">
              <div
                class="flex px-2 py-1 items-start gap-[10px] rounded border border-[#9E9EB7] bg-white text-[#16161D] text-center font-['Inter',sans-serif] text-[12px] font-normal leading-[14px] tracking-[0.07px] z-10 mx-1"
                :data-testid="`operator-badge-${groupIndex}`"
              >
                {{ operatorLabelFor() }}
              </div>
              <div class="h-[3px] bg-[#BCBCCD] grow"></div>
            </template>
          </div>

          <div
            :class="
              group.isOrGroup
                ? 'flex px-4 py-2 justify-center items-start rounded-lg border border-[#9E9EB7] bg-[#F0F0F4]'
                : 'flex flex-col items-center gap-1 min-w-[80px]'
            "
          >
            <template v-if="group.isOrGroup">
              <div class="flex items-start pt-2">
                <template v-for="(layer, layerIndex) in group.layers" :key="layer.id">
                  <div class="flex flex-col items-center gap-1 min-w-[80px]">
                    <div
                      class="w-10 h-10 rounded-full flex items-center justify-center z-10 bg-[#D32F2F]"
                    >
                      <v-icon :icon="mdiCheckCircle" color="white" size="22" />
                    </div>
                    <span class="text-[14px] text-[#4A4A4A] text-center leading-[1.2] mt-2">
                      Validação {{ getGlobalIndex(layer) }}
                    </span>
                    <div
                      v-for="role in layer.roles"
                      :key="role.id"
                      class="font-['Inter',sans-serif] font-bold text-[14px] text-[#16161D] text-center"
                    >
                      {{ VALIDATION_ROLE_LABELS[role.role] ?? role.role }}
                    </div>
                  </div>

                  <div
                    v-if="layerIndex < group.layers.length - 1"
                    class="flex px-2 py-1 items-start gap-[10px] rounded border border-[#9E9EB7] bg-white text-[#16161D] text-center font-['Inter',sans-serif] text-[12px] font-normal leading-[14px] tracking-[0.07px] z-10 mx-4 mt-[8px]"
                  >
                    OU
                  </div>
                </template>
              </div>
            </template>

            <template v-else>
              <div
                class="w-10 h-10 rounded-full flex items-center justify-center z-10 bg-[#D32F2F]"
              >
                <v-icon :icon="mdiCheckCircle" color="white" size="22" />
              </div>
              <span class="text-[14px] text-[#4A4A4A] text-center leading-[1.2] mt-2">
                Validação {{ getGlobalIndex(group.layers[0]) }}
              </span>
              <div
                v-for="role in group.layers[0].roles"
                :key="role.id"
                class="font-['Inter',sans-serif] font-bold text-[14px] text-[#16161D] text-center"
              >
                {{ VALIDATION_ROLE_LABELS[role.role] ?? group.layers[0].roles }}
              </div>
            </template>
          </div>
        </template>

        <div class="flex items-center min-w-[60px] grow h-10">
          <div class="h-[3px] bg-[#BCBCCD] grow"></div>
        </div>

        <div class="flex flex-col items-center gap-1 min-w-[80px]">
          <div class="w-10 h-10 rounded-full flex items-center justify-center z-10 bg-[#267D92]">
            <v-icon :icon="mdiAccountCircle" color="white" size="22" />
          </div>
          <span class="text-[14px] text-[#4A4A4A] text-center leading-[1.2] mt-2">
            Vaga<br />Atualizada
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { mdiCheckCircle, mdiPlay, mdiAccountCircle } from '@mdi/js'
import {
  LayerOperator,
  VALIDATION_ROLE_LABELS,
  LAYER_OPERATOR_LABELS
} from '@/types/approval-flow.types'
import type { ApprovalLayerDetail } from '@/Network/Types/Responses/ApprovalFlowResponses'

interface Props {
  layers: ApprovalLayerDetail[]
}

const props = defineProps<Props>()

const groupedLayers = computed(() => {
  const groups: { isOrGroup: boolean; layers: ApprovalLayerDetail[] }[] = []
  let validationCount = 0

  for (const layer of props.layers) {
    if (layer.operator === LayerOperator.OR) {
      const virtualLayers: ApprovalLayerDetail[] = layer.roles.map((role) => ({
        id: ++validationCount,
        order: layer.order,
        operator: layer.operator,
        roles: [role]
      }))
      if (virtualLayers.length > 0) {
        groups.push({ isOrGroup: true, layers: virtualLayers })
      }
    } else {
      for (const role of layer.roles) {
        groups.push({
          isOrGroup: false,
          layers: [
            {
              id: ++validationCount,
              order: layer.order,
              operator: layer.operator,
              roles: [role]
            }
          ]
        })
      }
    }
  }

  return groups
})

const getGlobalIndex = (layer: ApprovalLayerDetail) => layer.id

function operatorLabelFor(): string {
  return LAYER_OPERATOR_LABELS[LayerOperator.AND]
}
</script>
